import type { NavigateFunction } from "react-router-dom";
import type { Config } from "config/config";
import type { ABTestingService } from "services/abTesting";
import { ABTestName } from "services/abTesting/abTestingTypes";
import type { AnalyticsManager } from "services/analytics/analyticsManager";
import type { CompanyClient } from "services/company/companyClient";
import type { CompanyStore } from "services/company/companyStore";
import { type CompanyData, CompanyNamePostfix } from "services/company/companyTypes";
import type { CompanyProductsStore } from "services/companyProducts/companyProductsStore";
import { CompanyProduct } from "services/companyProducts/companyProductsTypes";
import type { LeadClient } from "services/lead/leadClient";
import { PricingPlan } from "services/pricingPlan/pricingPlanTypes";
import { SignUpPageStoreBase } from "services/signUpPage/signUpPageStoreBase";
import type { UserStore } from "services/user/userStore";
import { FlowType } from "services/user/userTypes";
import { UserCluster } from "services/userCluster/userClusterTypes";

const pricingPlanUrlParam = "pricing_plan";
const sameDayRegistrationUrlParam = "sameday_registration";

// Plans a caller-controlled ?pricing_plan= URL may select directly. Restricted plans
// (introducer_free_registration), deprecated plans, and experiment-internal variants
// must never be reachable from a public deep link.
const DEEP_LINKABLE_PRICING_PLANS: ReadonlySet<PricingPlan> = new Set([
  PricingPlan.JustRegister2026,
  PricingPlan.SecretaryLite2026,
  PricingPlan.SecretaryYearly2026,
  PricingPlan.TotalSupportYearly2026,
  PricingPlan.BusinessLaunch2026,
  PricingPlan.BusinessSupportYearly2026,
  PricingPlan.BusinessEssentials,
  PricingPlan.LaunchPack,
  PricingPlan.BlackFriday2025,
]);

export class SignUpPageStore extends SignUpPageStoreBase {
  protected _defaultPostfix = CompanyNamePostfix.Ltd;

  constructor(
    protected readonly _companyStore: CompanyStore,
    protected readonly _companyClient: CompanyClient,
    protected readonly _leadClient: LeadClient,
    protected readonly _analyticsManager: AnalyticsManager,
    protected readonly _userStore: UserStore,
    protected readonly _navigate: NavigateFunction,
    protected readonly _config: Config,
    private readonly _companyProductsStore: CompanyProductsStore,
    private readonly _abTestingService: ABTestingService,
  ) {
    super(_companyClient, _leadClient, _analyticsManager, _userStore, _navigate, _config);
  }

  protected async _proceedSignUp(): Promise<undefined | CompanyData> {
    if (this._abTestingService.isTreatment(ABTestName.ChargeWhenNoAccountExperiment)) {
      await this._userStore.tryCreateCluster(UserCluster.Cluster11);
    }

    const company = await super._proceedSignUp();
    const isNewUser: boolean = this._userStore.flowType === undefined;
    await this._updateFlow(company);

    const searchParams = this._extractSearchParams();
    const sameDayRegistration = searchParams.get(sameDayRegistrationUrlParam) === "true";

    await this._updatePricingPlan(searchParams, company, sameDayRegistration, isNewUser);
    await this._updateSameDayRegistration(sameDayRegistration, company);
    return company;
  }

  private async _updateFlow(company: CompanyData | undefined): Promise<boolean> {
    if (this._userStore.flowType || !company) {
      return true;
    }

    await this._userStore.updateCurrentFlow(FlowType.Incorporation);
    return false;
  }

  private async _updatePricingPlan(
    searchParams: URLSearchParams,
    company: CompanyData | undefined,
    isSameDayRegistration: boolean,
    isNewUser: boolean,
  ): Promise<void> {
    if (!company) {
      return;
    }

    const pricingPlan = searchParams.get(pricingPlanUrlParam); // 'basic' or 'anna_one'

    if (!pricingPlan) {
      return;
    }

    let resolvedPlan: PricingPlan | undefined;

    switch (pricingPlan) {
      case "basic":
        resolvedPlan = PricingPlan.JustRegister2026;
        break;
      case "essential":
        resolvedPlan = PricingPlan.BusinessEssentials;
        break;
      case "total_support":
        resolvedPlan = PricingPlan.TotalSupportYearly2026;
        break;
      case "business_support":
        resolvedPlan = PricingPlan.BusinessSupportYearly2026;
        break;
      case "launchpack":
        resolvedPlan = PricingPlan.LaunchPack;
        break;
      case "black_friday_2025":
        resolvedPlan = PricingPlan.BlackFriday2025;
        break;
      case "business_launch":
        resolvedPlan = PricingPlan.BusinessLaunch2026;
        break;
      default: {
        // The param may carry a plan id directly (e.g. 'secretary_lite_2026');
        // accept it only if it is on the deep-linkable allowlist, otherwise leave unassigned.
        const matchedPlan = Object.values(PricingPlan).find((plan) => plan === pricingPlan);
        if (matchedPlan && DEEP_LINKABLE_PRICING_PLANS.has(matchedPlan)) {
          resolvedPlan = matchedPlan;
        }
        break;
      }
    }

    if (resolvedPlan && !(await this._changePricingPlan(company, resolvedPlan))) {
      return;
    }

    // set the analytics
    if (isNewUser) {
      this._analyticsManager.event("pricing-plan-flow-started", {
        pricing_plan: pricingPlan || "default",
        same_day_registration: isSameDayRegistration,
      });
    } else {
      this._analyticsManager.event("pricing-plan-flow-updated", {
        pricing_plan: pricingPlan || "default",
        same_day_registration: isSameDayRegistration,
        existing_user: true,
      });
    }
  }

  private async _updateSameDayRegistration(
    isSameDayRegistration: boolean,
    company: CompanyData | undefined,
  ): Promise<void> {
    if (!company) {
      return;
    }

    if (isSameDayRegistration) {
      await this._companyStore.init(); // ensure company store is initialized
      await this._companyProductsStore.addProduct(CompanyProduct.SameDayRegistration);
    }
  }
}
